import { BO, BusinessObjectBase, CollectionChangedJoinPoint, FeatureFlagsRulesServiceBase, ModelBase, NonUniqueModelDataError, PropertyChangedJoinPoint, QueryBus, RepositoryResponseConflictCodes, ResponseConflictObject, ValidationResult, Weaver } from '@skylab-shared/core';
import { CalculationMethod, ExchangeRateModel, SaleDocumentItemModel, SalePatternIssueBusinessDayMode, SalePatternKsefSendOption, SalePatternModel, SalePatternSchedule, SplitPaymentState, monthlyScheduleKinds } from '@skylab-shared/dataModel';
import { MAX_MONTH_RANGE, dateDiffInDays, isNumber, sameValues } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../client/client.repo-base';
import { ContextKeys } from '../common/context-keys';
import { splitPaymentChecked } from '../common/document-utils';
import { EmailUtils } from '../common/email-utils';
import { propertyChangedJoinPointFilter } from '../common/property-changed/property-changed-filter';
import { EmailValidator } from '../common/validators/email-validator';
import { CommunityTransactionForSaleQuery } from '../community-transaction/queries/community-transaction-for-sale.query-base';
import { CompanyParametersQuery } from '../company/queries/company-parameters.query-base';
import { CurrencyCollectionQuery } from '../currency/queries/currency.query-base';
import { DocumentExchangeRateValidationBoPart } from '../documents/bo-parts/document-exchange-rate-validation.bo-part';
import { DocumentItemsNumberingBoPart } from '../documents/bo-parts/document-items-numbering.bo-part';
import { DocumentVatRateForSaleValidationBoPart } from '../documents/bo-parts/document-vat-rate-for-sale-validation.bo-part';
import { DocumentWstoBoPart } from '../documents/bo-parts/document-wsto.bo-part';
import { CommunityTransactionForSaleMixin } from '../documents/mixins/community-transaction-for-sale.mixin';
import { RecalculateDocumentJoinPoint, RecalculateDocumentJoinPointContext } from '../documents/utils/recalculate-document-join-point';
import { WrongPaymentMethodOnDocumentWithSplitPaymentWarning } from '../documents/violations/wrong-payment-method-on-document-with-split-payment-warning';
import { NumberingQuery } from '../numbering/queries/numbering.query-base';
import { PaymentMethodQuery } from '../payment-method/queries/payment-method.query-base';
import { ProductRepositoryContract } from '../shared-business-temporary-contracts';
import { UserCollectionQuery } from '../user/queries/user.query-base';
import { collectionChangedJoinPointFilter } from '../utils/collection-changed-filter';
import { VatRateForSaleQuery } from '../vat/queries/vat-rate-for-sale.query-base';
import { VatReleaseReasonQuery } from '../vat/queries/vat-release-reason.query-base';
import { BankAccountNumberMixin } from './mixins/bank-account.mixin';
import { ClientMixin } from './mixins/client.mixin';
import { SaleDocumentCalculatorBoPart } from './parts/sale-document-calculator.bo-part';
import { SaleDocumentClientBoPart } from './parts/sale-document-client.bo-part';
import { SaleDocumentItemsValidationsBoPart } from './parts/sale-document-items-validations.bo-part';
import { SaleDocumentPaymentsBoPart } from './parts/sale-document-payment.bo-part';
import { SaleDocumentReverseChargeAndNPBoPart } from './parts/sale-document-reverse-charge-and-NP.bo-part';
import { SplitPaymentLimitInPln } from './parts/sale-document-split-payment.bo-part';
import { SaleDocumentVatReleaseBoPart } from './parts/sale-document-vat-release.bo-part';
import { SaleKindQuery } from './queries/sale-kind.query-base';
import { SplitPaymentFlag } from './sale-feature-flags-rules.service-base';
import { refreshSaleDocumentItemPriceAsync } from './utils/sale-utils';
import { ClientEmailEmptyError } from './violations/client-email-empty-error';
import { DueDateInDaysLessThanZeroError } from './violations/due-date-in-days-less-than-zero-error';
import { InvalidEmailError } from './violations/invalid-email-error';
import { IssueDayOutOfRangeError } from './violations/issue-day-out-of-range-error';
import { IssueEndingDateInvalidWarning } from './violations/issue-ending-date-invalid-warning';
import { IssueStartDateInvalidWarning } from './violations/issue-start-date-invalid-error';
import { NumberingMustNotBeEmptyError } from './violations/numbering-must-not-be-empty-error';
import { NumberingShouldBeActive } from './violations/numbering-should-be-active-error';
import { RepetitionSkipAllowedOnlyForMonthlyScheduleError } from './violations/repetition-skip-allowed-only-for-monthly-schedule-error';
import { RepetitionSkipForMonthlyScheduleOutOfRangeError } from './violations/repetition-skip-for-monthly-schedule-out-of-range-error';
import { SplitPaymentOnNonDomesticSaleError } from './violations/split-payment-on-non-domestic-sale-error';
import { WrongCalculationMethodUsedOnDocumentError } from './violations/wrong-calculation-method-used-on-document-error';

export interface SalePatternBO extends CommunityTransactionForSaleMixin<SalePatternModel>, BankAccountNumberMixin<SalePatternModel>, ClientMixin<SalePatternModel> {};

@BO({
    modelType: SalePatternModel,
    mixins: [CommunityTransactionForSaleMixin, BankAccountNumberMixin, ClientMixin]
})
export class SalePatternBO extends BusinessObjectBase<SalePatternModel> {
    public constructor(
        weaver: Weaver,
        protected readonly productRepository: ProductRepositoryContract,
        protected readonly clientRepository: ClientRepositoryBase,
        protected readonly featureFlagsRulesService: FeatureFlagsRulesServiceBase,
        protected readonly queryBus: QueryBus
    ) {
        super(weaver);
        this.initBOParts();
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.resolve([
            this.localContext.preload(CurrencyCollectionQuery),
            this.localContext.preload(PaymentMethodQuery),
            this.localContext.preload(SaleKindQuery),
            this.localContext.preload(NumberingQuery),
            this.localContext.preload(VatRateForSaleQuery),
            this.localContext.preload(CommunityTransactionForSaleQuery),
            this.localContext.preload(UserCollectionQuery),
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(ContextKeys.company),
            this.localContext.preload(VatReleaseReasonQuery)
        ]);

        if (this.model.isPersistent) {
            await this.loadSourceClient();
        }

        this.registerValidationRules();
        this.registerPropretyChanged();
    }

    private initBOParts(): void {
        this.parts.push(...[
            new SaleDocumentItemsValidationsBoPart(this.weaver, this.productRepository, this.featureFlagsRulesService, this.queryBus),
            new DocumentVatRateForSaleValidationBoPart(this.weaver),
            new DocumentItemsNumberingBoPart(this.weaver),
            new SaleDocumentCalculatorBoPart(this.weaver),
            new SaleDocumentClientBoPart(this.weaver, this.featureFlagsRulesService),
            new SaleDocumentPaymentsBoPart(this.weaver),
            new SaleDocumentReverseChargeAndNPBoPart(this.weaver),
            new DocumentWstoBoPart(this.weaver, this.queryBus),
            new SaleDocumentVatReleaseBoPart(this.weaver, this.queryBus),
            new DocumentExchangeRateValidationBoPart(this.weaver)
        ]);
    }

    private registerPropretyChanged(): void {
        this.isEmailValid();
        this.updateBankAccountsNumbers();
        this.applyDiscountToNewItemOnCollectionChanged();
        this.updateDayToIssue();
    }

    private registerValidationRules(): void {
        this.registerIssueDayValidationRules();
        this.registerIssueRangeValidationRules();
        this.registerEmailValidationRules();
        this.registerCalculationMethodValidationRules();
        this.registerPaymentDateValidationRules();
        this.registerPaymentMethodValidationRules();
        this.registerSplitPaymentValidationRules();
        this.registerRepetitionSkipValidationRules();
        this.registerSplitPaymentChecker();
        this.registerNumberingsValidationRules();
    }

    protected override handleConflictSaveResponse(conflict: ResponseConflictObject<RepositoryResponseConflictCodes>) {
        if ((conflict.code === RepositoryResponseConflictCodes.UniqueConstraintViolation) && sameValues(conflict.objectProperties as unknown as Array<string>, ['name'])) {
            this.addExternalRuleViolations({
                [this.model.sun]: [{
                    property: 'name',
                    violation: new NonUniqueModelDataError('business.document.sale.validation.pattern-name.non-unique')
                }]
            });
        }
    }

    private registerNumberingsValidationRules(): void {
        this.validation.addRule(
            [[SalePatternModel, 'numberingId']],
            ctx => !!ctx.source.numberingId,
            NumberingMustNotBeEmptyError
        );

        this.validation.addRule(
            [[SalePatternModel, 'numberingId'], [SalePatternModel, 'active']],
            [[SalePatternModel, 'numberingId']],
            ctx => {
                if (!ctx.source.active) return true;

                const numbering = this.localContext.firstPreloaded(NumberingQuery, { id: ctx.source.numberingId})!;
                return !!numbering?.active;
            },
            NumberingShouldBeActive
        );
    }

    private registerIssueDayValidationRules(): void {
        this.validation.addRule(
            [[SalePatternModel, 'issueDay']],
            ctx => !(isNumber(ctx.source.issueDay) && (ctx.source.issueDay < MAX_MONTH_RANGE.START || ctx.source.issueDay > MAX_MONTH_RANGE.END)),
            IssueDayOutOfRangeError
        );
    }

    private registerIssueRangeValidationRules(): void {
        this.validation.addRule(
            [[SalePatternModel, 'issuingStartDate'], [SalePatternModel, 'issuingEndDate']],
            [[SalePatternModel, 'issuingEndDate']],
            ctx => {
                if (ctx.source.issuingEndDate) {
                    if (ctx.source.issuingStartDate) {
                        return dateDiffInDays(ctx.source.issuingEndDate, ctx.source.issuingStartDate) >= 0;
                    } else if (!this.model.isPersistent || this.model.modifiedProperties.has('issuingEndDate')) {
                        return dateDiffInDays(ctx.source.issuingEndDate, new Date()) > 0;
                    }
                }
                return true;
            },
            IssueEndingDateInvalidWarning
        );

        this.validation.addRule(
            [[SalePatternModel, 'issuingStartDate']],
            ctx => {
                if (!ctx.source.issuingStartDate || (this.model.isPersistent && !this.model.modifiedProperties.has('issuingStartDate'))) return true;
                return dateDiffInDays(ctx.source.issuingStartDate, new Date()) >= 0;

            },
            IssueStartDateInvalidWarning
        );
    }

    private registerEmailValidationRules(): void {
        this.validation.addRule(
            [[SalePatternModel, 'clientEmails'], [SalePatternModel, 'sendDocumentToClient']],
            [[SalePatternModel, 'clientEmails']],
            ctx => !(ctx.source.sendDocumentToClient && ctx.source.clientEmails.length === 0),
            ClientEmailEmptyError
        );
    }

    private registerCalculationMethodValidationRules(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SalePatternModel, ['calculationMethod'])(
            () => this.validation.queueValidation([SaleDocumentItemModel, 'discountValue'])
        )));

        this.validation.addRule(
            [[SalePatternModel, 'calculationMethod'], [SalePatternModel, 'issuedByVatPayer']],
            [[SalePatternModel, 'calculationMethod']],
            ctx => {
                if (!ctx.source.issuedByVatPayer && ctx.source.calculationMethod !== CalculationMethod.Gross) return false;
                return [CalculationMethod.Net, CalculationMethod.Gross].includes(ctx.source.calculationMethod);
            },
            WrongCalculationMethodUsedOnDocumentError
        );
    }

    private registerPaymentDateValidationRules(): void {
        this.validation.addRule(
            [[SalePatternModel, 'dueDateInDays']],
            ctx => ctx.source.dueDateInDays >= 0,
            DueDateInDaysLessThanZeroError
        );
    }

    private registerPaymentMethodValidationRules(): void {
        this.validation.addRule(
            [[SalePatternModel, 'splitPayment'], [SalePatternModel, 'paymentMethodId']],
            [[SalePatternModel, 'paymentMethodId']],
            ctx => {
                const bankTransferIds = this.localContext.filterPreloaded(PaymentMethodQuery, {
                    cashPayment: false,
                    associatedWithBankAccount: true
                }).map(({ id }) => id);

                const splitPayment = splitPaymentChecked(ctx.source.splitPayment);
                return (splitPayment && bankTransferIds.includes(ctx.source.paymentMethodId)) || !splitPayment;
            },
            WrongPaymentMethodOnDocumentWithSplitPaymentWarning
        );
    }

    private registerRepetitionSkipValidationRules(): void {
        this.validation.addRule(
            [[SalePatternModel, 'schedule'], [SalePatternModel, 'repetitionSkip']],
            [[SalePatternModel, 'repetitionSkip']],
            ctx => ctx.source.repetitionSkip == null
                || (monthlyScheduleKinds.includes(ctx.source.schedule as any) && ctx.source.repetitionSkip > 0 && ctx.source.repetitionSkip <= 12)
                || !monthlyScheduleKinds.includes(ctx.source.schedule as any),
            RepetitionSkipForMonthlyScheduleOutOfRangeError
        );

        this.validation.addRule(
            [[SalePatternModel, 'schedule'], [SalePatternModel, 'repetitionSkip']],
            [[SalePatternModel, 'repetitionSkip']],
            ctx => !ctx.source.repetitionSkip || monthlyScheduleKinds.includes(ctx.source.schedule as any),
            RepetitionSkipAllowedOnlyForMonthlyScheduleError
        );
    }

    private registerSplitPaymentValidationRules(): void {
        this.validation.addRule(
            [[SalePatternModel, 'communityTransactionId'], [SalePatternModel, 'splitPayment']],
            [[SalePatternModel, 'splitPayment']],
            ctx => {
                if (!this.model.issuedByVatPayer) return true;
                const { id: skId } = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { symbol: 'SK' })!;
                const splitPayment = splitPaymentChecked(ctx.source.splitPayment);
                return (splitPayment && ctx.source.communityTransactionId === skId) || !splitPayment;
            },
            SplitPaymentOnNonDomesticSaleError
        );
    }

    private registerSplitPaymentChecker(): void {
        this.registerListening(this.weaver.register(
            PropertyChangedJoinPoint,
            propertyChangedJoinPointFilter(this)(SalePatternModel, ['communityTransactionId', 'grossValue'])(this.checkSplitPaymentAsync.bind(this))
        ));

        this.registerListening(this.weaver.register(
            CollectionChangedJoinPoint,
            collectionChangedJoinPointFilter(this)(this.model.items)(this.checkSplitPaymentAsync.bind(this))
        ));

        this.registerListening(this.weaver.register(
            PropertyChangedJoinPoint,
            propertyChangedJoinPointFilter(this)(ExchangeRateModel, ['rate', 'units'])(this.checkSplitPaymentAsync.bind(this))
        ));
    }

    private async checkSplitPaymentAsync(): Promise<void> {
        if (!this.isSplitPaymentAllowed) return;

        const { id: skId } = this.localContext.firstPreloaded(CommunityTransactionForSaleQuery, { symbol: 'SK' })!;
        let splitPayment = false;

        if (this.model.communityTransactionId === skId && this.model.grossValue > SplitPaymentLimitInPln) {
            const productsLoading = this.model.items
                .filter(item => typeof item.productId === 'number')
                .map(item => this.localContext.getAsync(ContextKeys.product(item.productId!), () => this.productRepository.findAsync(item.productId!)));
            const products = await Promise.all(productsLoading);
            splitPayment = products.some(product => product.subjectToReverseCharge);
        }

        if (this.model.splitPayment !== SplitPaymentState.Manual) {
            this.model.splitPayment = splitPayment ? SplitPaymentState.True : SplitPaymentState.False;
        }
    }

    private get isSplitPaymentAllowed(): boolean {
        return this.model.issuedByVatPayer && this.featureFlagsRulesService.getFlagStatus(SplitPaymentFlag, new Date());
    }

    private isEmailValid(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SalePatternModel, ['clientEmails'])(
            ctx => {
                this.validation.removeExternalValidationResults('email-validation');
                if (!EmailValidator.isValidEmails(EmailUtils.concatEmails(this.model.clientEmails))) {
                    const invalidEmails = EmailUtils.concatEmails(EmailValidator.getInvalidEmailsFromArray(this.model.clientEmails));
                    if (!invalidEmails) return;

                    this.validation.addExternalValidationResult(<ValidationResult<ModelBase>>{
                        source: this.model,
                        propertyName: <keyof SalePatternModel>'clientEmails',
                        ruleViolation: {
                            rule: null,
                            context: null,
                            violation: new InvalidEmailError({ emails: invalidEmails })
                        }
                    }, 'email-validation');
                }
            }
        )));
    }

    private updateBankAccountsNumbers(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SalePatternModel, ['currencyId', 'communityTransactionId', 'paymentMethodId'])(
            _ => this.updateBankAccountNumber()
        )));
    }

    private applyDiscountToNewItemOnCollectionChanged(): void {
        const reactToDocumentItemsCollectionChange = collectionChangedJoinPointFilter(this)(this.model.items);
        this.registerListening(this.weaver.register(CollectionChangedJoinPoint, reactToDocumentItemsCollectionChange(
            ctx => (ctx.newItems as any[]).forEach((item: SaleDocumentItemModel) => this.applyClientDiscountToItem(item))
        )));
    }

    private updateDayToIssue(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(SalePatternModel, ['schedule'])(
            _ => {
                const schedule = this.model.schedule as SalePatternSchedule;
                if ([SalePatternSchedule.Weekly, ...monthlyScheduleKinds].includes(schedule)) {
                    this.model.issueDay = 1;
                    this.model.issueMonth = null;
                } else if (schedule === SalePatternSchedule.Yearly) {
                    const date = new Date();
                    this.model.issueDay = date.getDate();
                    this.model.issueMonth = date.getMonth() + 1;
                } else {
                    this.model.issueDay = null;
                    this.model.sendEmailToIssuer = false;
                    this.model.issuingEndDate = null;
                    this.model.issueInBusinessDay = false;
                    this.model.issueInBusinessDayMode = SalePatternIssueBusinessDayMode.AccordingTo;
                    this.model.ksefSendOption = SalePatternKsefSendOption.AccordingToConfiguration;
                }
            })));
    }

    public async refreshDocumentItemsPricesAsync(): Promise<void> {
        await Promise.all(this.model.items.map(async item => await refreshSaleDocumentItemPriceAsync(item, this, this.productRepository, { isSystemCurrencyPriceDefault: false })));
    }

    public inForeignCurrency(): boolean {
        const { systemCurrency } = this.localContext.getPreloaded(CompanyParametersQuery)!;
        const pln = this.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
        return this.model.currencyId !== pln!.id;
    }

    public triggerDocumentRecalculation(): void {
        const documentRecalculationCtx = new RecalculateDocumentJoinPointContext(this);
        this.weaver.weave(new RecalculateDocumentJoinPoint(documentRecalculationCtx));
    }

    public clearClient(): void {
        this.clearClient();
        this.model.clientEmails = [];
    }
}
